import React, { useEffect, useRef } from 'react';
import {
  useTable,
  useExpanded,
  useRowSelect,
  usePagination,
  useResizeColumns,
  useSortBy,
  useFlexLayout,
  useAsyncDebounce,
} from 'react-table';
import { useSticky } from 'react-table-sticky';

import { useUpdateEffect } from '@/hooks';
import { saveInvoke } from '@/utils';

import '@/style/components/DataTable/DataTable.scss';

import TableNoResultsRow from './TableNoResultsRow';
import TableLoadingRow from './TableLoading';
import TableHeader from './TableHeader';
import TablePage from './TablePage';
import TableFooter from './TableFooter';
import TableRow from './TableRow';
import TableRows from './TableRows';
import TableCell from './TableCell';
import TableTBody from './TableTBody';
import TableContext from './TableContext';
import TablePagination from './TablePagination';
import TableWrapper from './TableWrapper';

import TableIndeterminateCheckboxRow from './TableIndeterminateCheckboxRow';
import TableIndeterminateCheckboxHeader from './TableIndeterminateCheckboxHeader';

import { useResizeObserver } from './utils';

interface DataTableProps {
  columns: any;
  data: any;
  [key: string]: any;
}

/**
 * Datatable component.
 */
export function DataTable(props: DataTableProps) {
  const {
    columns,
    data,

    onFetchData,

    onSelectedRowsChange,
    manualSortBy = false,
    manualPagination = true,
    selectionColumn = false,
    expandSubRows = true,
    expanded = {},
    rowClassNames,
    payload = {},
    expandable = false,
    noInitialFetch = false,
    noResults = '',

    pagination = false,
    hidePaginationNoPages = true,
    hideTableHeader = false,
    size = null,
    spinnerProps = { size: 30 },
    expandToggleColumn = 1,
    expandColumnSpace = 0.8,

    // `-1` = nombre de pages inconnu. Il ne faut jamais laisser `undefined`
    // arriver jusqu'à react-table : le réducteur `gotoPage` compare l'index
    // demandé à `instance.pageCount`, et `1 < undefined` est faux — la
    // navigation est alors silencieusement refusée (page qui ne change pas).
    pagesCount: controlledPageCount = -1,

    // Pagination props.
    initialPageIndex = 0,
    initialPageSize = 20,

    // Hidden columns.
    initialHiddenColumns = [],

    updateDebounceTime = 200,
    selectionColumnWidth = 42,

    autoResetPage = true,
    autoResetExpanded = true,
    autoResetGroupBy = true,
    autoResetSelectedRows = true,
    autoResetSortBy = true,
    autoResetFilters = true,
    autoResetRowState = true,

    // Components
    TableHeaderRenderer = TableHeader,
    TablePageRenderer = TablePage,
    TableWrapperRenderer = TableWrapper,
    TableTBodyRenderer = TableTBody,
    TablePaginationRenderer = TablePagination,
    TableFooterRenderer = TableFooter,
    TableLoadingRenderer = TableLoadingRow,
    TableRowsRenderer = TableRows,
    TableRowRenderer = TableRow,
    TableCellRenderer = TableCell,
    TableNoResultsRowRenderer = TableNoResultsRow,

    onColumnResizing,
    initialColumnsWidths,

    ...restProps
  } = props;

  const selectionColumnObj = {
    id: 'selection',
    disableResizing: true,
    minWidth: selectionColumnWidth,
    width: selectionColumnWidth,
    maxWidth: selectionColumnWidth,
    skeletonWidthMin: 100,
    // The header can use the table's getToggleAllRowsSelectedProps method
    // to render a checkbox
    Header: TableIndeterminateCheckboxHeader,
    // The cell can use the individual row's getToggleRowSelectedProps method
    // to the render a checkbox
    Cell: TableIndeterminateCheckboxRow,
    className: 'selection',
    ...(typeof selectionColumn === 'object' ? selectionColumn : {}),
  };

  const table = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: initialPageIndex,
        pageSize: initialPageSize,
        expanded,
        columnResizing: {
          columnWidths: initialColumnsWidths || {},
        },
        hiddenColumns: initialHiddenColumns,
      },
      manualPagination,
      pageCount: controlledPageCount,
      getSubRows: (row: any) => row.children,
      manualSortBy,
      expandSubRows,
      payload,

      autoResetPage,
      autoResetExpanded,
      autoResetGroupBy,
      autoResetSelectedRows,
      autoResetSortBy,
      autoResetFilters,
      autoResetRowState,

      ...restProps,
    },
    useSortBy,
    useExpanded,
    useResizeColumns,
    useFlexLayout,
    useSticky,
    usePagination,
    useRowSelect,
    (hooks: any) => {
      hooks.visibleColumns.push((columns: any[]) => [
        // Let's make a column for selection
        ...(selectionColumn ? [selectionColumnObj] : []),
        ...columns,
      ]);
    },
  );

  const {
    selectedFlatRows,
    state: { pageIndex, pageSize, sortBy, selectedRowIds },
  } = table;

  const isInitialMount = useRef(noInitialFetch);

  const onFetchDataDebounced = useAsyncDebounce((...args: any[]) => {
    saveInvoke(onFetchData, ...args);
  }, updateDebounceTime);

  const onFetchDataDebouncedRef = useRef(onFetchDataDebounced);
  onFetchDataDebouncedRef.current = onFetchDataDebounced;

  // When these table states change, fetch new data!
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      onFetchDataDebouncedRef.current({ pageIndex, pageSize, sortBy });
    }
    // onFetchDataDebounced is intentionally excluded — useAsyncDebounce creates
    // a new reference every render, adding it here causes an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize, sortBy]);

  const onSelectedRowsChangeRef = useRef(onSelectedRowsChange);
  onSelectedRowsChangeRef.current = onSelectedRowsChange;

  useUpdateEffect(() => {
    saveInvoke(onSelectedRowsChangeRef.current, selectedFlatRows);
    // onSelectedRowsChange intentionally excluded — it's an inline function that
    // changes every render, which would cause an infinite loop via Redux dispatch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowIds]);

  // Column resizing observer.
  useResizeObserver(
    table.state,
    (current: any, columnWidth: any, columnsResizing: any) => {
      onColumnResizing &&
        onColumnResizing(current, columnWidth, columnsResizing);
    },
  );

  // Merge raw props with resolved defaults so context consumers get correct values.
  const resolvedProps = {
    ...props,
    payload,
    noResults,
    pagination,
    hidePaginationNoPages,
    hideTableHeader,
    size,
    spinnerProps,
    expandToggleColumn,
    expandColumnSpace,
    TableHeaderRenderer,
    TablePageRenderer,
    TableWrapperRenderer,
    TableTBodyRenderer,
    TablePaginationRenderer,
    TableFooterRenderer,
    TableLoadingRenderer,
    TableRowsRenderer,
    TableRowRenderer,
    TableCellRenderer,
    TableNoResultsRowRenderer,
  };

  return (
    <TableContext.Provider value={{ table, props: resolvedProps }}>
      <TableWrapperRenderer>
        <TableHeaderRenderer />

        <TableTBodyRenderer>
          <TablePageRenderer />
        </TableTBodyRenderer>

        <TableFooterRenderer />
      </TableWrapperRenderer>

      <TablePaginationRenderer />
    </TableContext.Provider>
  );
}
